'use es6';

import { fromJS, List as ImmutableList } from 'immutable';
import { ANALYTICS_SUB_SUITE_IDS } from 'reporting-discover-components/constants/AnalyticsSubSuiteIds';
import { COLLECTION_ACCESS } from '../constants/accessControl';
import { checkAccessRequirements } from '../utils/accessControlUtils';
import { ANALYTICS_SUITE_IDS, REVENUE_SUB_APP } from '../constants/templates';
import { SUB_APPS } from 'reporting-data/tsTypes/subAppTypes';
import { getIsUngatedToActivitiesAndLeadsNewIA, isAccessingRevenueThroughService, isUngatedToImprovedRecurringRevenue } from '../utils/accessUtils';
import { ANALYTICS_SUITE_TAB_IDS } from '../SmartLibrary/types/navigation';

// in order of top level to lower level nav item types
export const NAV_ITEM_HIERARCHY_ARRAY = ['collection', 'category', 'template'];
const NEW_ACTIVITIES_AND_LEADS_ORDER = fromJS([{
  id: SUB_APPS.activities
}, {
  id: SUB_APPS.teamActivity
}, {
  id: SUB_APPS.activityStream
}, {
  id: SUB_APPS.calls
}, {
  id: SUB_APPS.conversations
}, {
  id: SUB_APPS.emailRates
}, {
  id: SUB_APPS.leads
}, {
  id: SUB_APPS.meetings
}, {
  id: SUB_APPS.sequences
}, {
  id: SUB_APPS.tasks
}]);
const BASE_ANALYTIC_TOOLS_IA = fromJS([{
  id: ANALYTICS_SUITE_IDS.MARKETING,
  children: [{
    id: ANALYTICS_SUB_SUITE_IDS.WEB_TRAFFIC_ANALYSIS,
    children: [{
      id: SUB_APPS.sources
    }, {
      id: SUB_APPS.pages
    }, {
      id: SUB_APPS.utmParameters
    }, {
      id: SUB_APPS.deviceTypes
    }, {
      id: SUB_APPS.geolocation
    }, {
      id: SUB_APPS.browsers
    }, {
      id: SUB_APPS.topicClusters
    }]
  }, {
    id: ANALYTICS_SUB_SUITE_IDS.ADVANCED_MARKETING_REPORTING,
    children: [{
      id: SUB_APPS.customerJourneyAnalytics
    }, {
      id: SUB_APPS.attribution
    }]
  }, {
    id: ANALYTICS_SUB_SUITE_IDS.CHANNEL_PERFORMANCE,
    children: [{
      id: SUB_APPS.ads
    }, {
      id: SUB_APPS.cta
    }, {
      id: SUB_APPS.leadCapture
    }, {
      id: SUB_APPS.marketingEmails
    }, {
      id: SUB_APPS.sms
    }, {
      id: SUB_APPS.social
    }]
  }, {
    id: ANALYTICS_SUB_SUITE_IDS.CRM_INSIGHTS,
    children: [{
      id: SUB_APPS.contactInsights
    }]
  }]
}, {
  id: ANALYTICS_SUITE_IDS.SALES,
  children: [{
    id: ANALYTICS_SUB_SUITE_IDS.ACTIVITIES_AND_LEADS,
    children: [{
      id: SUB_APPS.callOutcomes
    }, {
      id: SUB_APPS.conversations
    }, {
      id: SUB_APPS.activities
    }, {
      id: SUB_APPS.emailRates
    }, {
      id: SUB_APPS.leadFunnel
    }, {
      id: SUB_APPS.leadResponseTime
    }, {
      id: SUB_APPS.meetingOutcomes
    }, {
      id: SUB_APPS.prospectEngagement
    }, {
      id: SUB_APPS.taskOutcomes
    }, {
      id: SUB_APPS.teamActivity
    }, {
      id: SUB_APPS.activityStream
    }]
  }, {
    id: ANALYTICS_SUB_SUITE_IDS.DEALS,
    children: [{
      id: SUB_APPS.averageSalesPrice
    }, {
      id: SUB_APPS.changeHistoryDealList
    }, {
      id: SUB_APPS.dealsCreatedByRep
    }, {
      id: SUB_APPS.dealJourney
    }, {
      id: SUB_APPS.lossReasons
    }, {
      id: SUB_APPS.waterfall
    }, {
      id: SUB_APPS.dealPushAnalysis
    }, {
      id: SUB_APPS.dealVelocity
    }, {
      id: SUB_APPS.dealsWon
    }, {
      id: SUB_APPS.salesVelocity
    }, {
      id: SUB_APPS.timeInDealStage
    }]
  }, {
    id: ANALYTICS_SUB_SUITE_IDS.FORECAST_AND_REVENUE,
    children: [{
      id: SUB_APPS.salesBySource
    }, {
      id: SUB_APPS.manualForecastTotal
    }, {
      id: SUB_APPS.pipelineSnapshots
    }, {
      id: SUB_APPS.quotaAttainment
    }, {
      id: SUB_APPS.manualForecast
    }, {
      id: SUB_APPS.weightedPipelineForecast
    }]
  }]
}, {
  id: ANALYTICS_SUITE_IDS.SERVICE,
  children: [{
    id: ANALYTICS_SUB_SUITE_IDS.CUSTOMER_SATISFACTION,
    children: [{
      id: SUB_APPS.postSupportCSAT
    }, {
      id: SUB_APPS.customerEffortScore
    }]
  }, {
    id: ANALYTICS_SUB_SUITE_IDS.TEAM_PERFORMANCE,
    children: [{
      id: SUB_APPS.ticketTimeToClose
    }, {
      id: SUB_APPS.ticketTimeToRespond
    }, {
      id: SUB_APPS.queueTime
    }, {
      id: SUB_APPS.supportVolume
    }, {
      id: SUB_APPS.ticketEnteredNew
    }, {
      id: SUB_APPS.ticketEnteredWaiting
    }, {
      id: SUB_APPS.ticketTimeInPipeline
    }]
  }, {
    id: ANALYTICS_SUB_SUITE_IDS.TICKET_REPLIES,
    children: [{
      id: SUB_APPS.customerReplyTime
    }, {
      id: SUB_APPS.customerWaitTime
    }, {
      id: SUB_APPS.ownerReplyCount
    }, {
      id: SUB_APPS.ownerReplyTime
    }, {
      id: SUB_APPS.ticketReplyCount
    }, {
      id: SUB_APPS.timeOfDay
    }]
  }, {
    id: ANALYTICS_SUB_SUITE_IDS.SERVICE_PLANNING,
    children: [{
      id: SUB_APPS.workingHoursAvailability
    }, {
      id: SUB_APPS.maxAvailableRep
    }, {
      id: SUB_APPS.repAvailability
    }]
  }, {
    id: ANALYTICS_SUB_SUITE_IDS.SELF_SERVICE_PERFORMANCE,
    children: [{
      id: SUB_APPS.knowledgeBaseToTickets
    }, {
      id: SUB_APPS.chatbotAndCustomerAgentHandoffs
    }, {
      id: SUB_APPS.chatbotHandoffs
    }, {
      id: SUB_APPS.ticketsToDeals
    }]
  }]
}, {
  id: ANALYTICS_SUITE_IDS.COMMERCE,
  children: [{
    id: ANALYTICS_SUB_SUITE_IDS.INVOICES,
    children: [{
      id: SUB_APPS.agingSummary
    }, {
      id: SUB_APPS.balanceDue
    }, {
      id: SUB_APPS.expectedVsActualRevenue
    }, {
      id: SUB_APPS.invoiceStatus
    }]
  }, {
    id: ANALYTICS_SUB_SUITE_IDS.SUBSCRIPTIONS,
    children: [{
      id: SUB_APPS.commerceChurnSubscriptions
    }, {
      id: SUB_APPS.commerceChurnSubscriptionsCount
    }, {
      id: SUB_APPS.commerceNewSubscriptions
    }, {
      id: SUB_APPS.commerceNewSubscriptionsCount
    }]
  }, {
    id: ANALYTICS_SUB_SUITE_IDS.PAYMENTS,
    children: [{
      id: SUB_APPS.grossPaymentRevenue
    }, {
      id: SUB_APPS.totalPaymentVolume
    }]
  }]
}]);
export const getAnalyticsToolsIA = (userGates, userScopes) => {
  const makeTool = id => fromJS({
    id
  });
  const IAwithReorderedActivitiesAndLeads = getIsUngatedToActivitiesAndLeadsNewIA(userGates) ? BASE_ANALYTIC_TOOLS_IA.setIn([1, 'children', 0, 'children'], NEW_ACTIVITIES_AND_LEADS_ORDER) : BASE_ANALYTIC_TOOLS_IA;
  const IAwithRevenue = isAccessingRevenueThroughService(userScopes) ? IAwithReorderedActivitiesAndLeads.updateIn([2, 'children'], lst => lst.push(makeTool(REVENUE_SUB_APP))) : IAwithReorderedActivitiesAndLeads.updateIn([1, 'children', 2, 'children'], lst => lst.insert(4, makeTool(REVENUE_SUB_APP)));
  const IAwithLineItemRevenue = isUngatedToImprovedRecurringRevenue(userGates) ? IAwithRevenue.updateIn([1, 'children', 2, 'children'], lst => lst.insert(4, makeTool(SUB_APPS.lineItemRevenue))) : IAwithRevenue;
  return IAwithLineItemRevenue;
};
const hasNavItemAccess = (navItemId, gates, scopes) => {
  const accessControl = COLLECTION_ACCESS.get(navItemId);
  return accessControl ? checkAccessRequirements({
    gates,
    scopes,
    accessControl
  }).accessAllowed : true;
};
export const getAllCategoryReports = (category, gates, scopes) => {
  if (!category) {
    return null;
  }
  const categoryChildren = category.get('children');
  const reports = categoryChildren && categoryChildren.reduce((acc, curr) => {
    const report = curr.get('id');
    return hasNavItemAccess(report, gates, scopes) ? acc.push({
      parentId: category.get('id'),
      report
    }) : acc;
  }, ImmutableList());

  //For items that don't have a category I.e. contact insights
  if (!categoryChildren) {
    const reportId = category.get('id');
    if (hasNavItemAccess(reportId, gates, scopes)) {
      return ImmutableList([{
        parentId: category.get('id'),
        report: category.get('id')
      }]);
    }
  }
  return reports || ImmutableList();
};
export const getAllCollectionReports = (collectionId, gates, scopes) => {
  const activeIA = getAnalyticsToolsIA(gates, scopes);
  if (!collectionId) {
    return null;
  }
  let collection;
  if (collectionId === ANALYTICS_SUITE_TAB_IDS.DISCOVER) {
    //used to include all suite children in recommendations logic
    collection = fromJS({
      children: activeIA.flatMap(curr => curr.get('children') || ImmutableList())
    });
  } else {
    collection = activeIA.find(collections => collections.get('id') === collectionId);
  }
  const allFilteredReports = collection.get('children').reduce((acc, curr) => {
    if (hasNavItemAccess(curr.get('id'), gates, scopes)) {
      /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
      return acc.concat(getAllCategoryReports(curr, gates, scopes));
    }
    return acc;
  }, ImmutableList());
  return allFilteredReports;
};
export const findCategoryParent = (collectionId, templateId, gates, scopes) => {
  const activeIA = getAnalyticsToolsIA(gates, scopes);
  const collectionIA = activeIA.find(collections => collections.get('id') === collectionId);
  if (!collectionIA.get('children')) return null;
  const categoryIA = collectionIA && collectionIA.get('children').find(category => {
    const allCategoryChildren = getAllCategoryReports(category, gates, scopes);
    return allCategoryChildren.find(report => report.report === templateId);
  });
  return categoryIA;
};